import { IAntDProTableRequest } from "../../../general/type/general.types";
import { apiRequest, downloadFile } from "../../../services/api";
import { getUser } from "../../../services/auth";
import { openOrDownloadBlob } from "../../../utils/fileDownload";
import {
  IFileRequest,
  IApiResponseGetFiles,
  IFileStatusRequest,
  IFileProfitRequest,
} from "../type/file";

export const getFiles = async (
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetFiles> => {
  const response = await apiRequest("GET", "/files", tableRequest);
  return response.data;
};

export const getFileByInternalUser = async (
  id: string,
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetFiles> => {
  const response = await apiRequest("GET", `/files/user/${id}`, tableRequest);
  return response.data;
};

export const getFileByClientId = async (
  id: string,
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetFiles> => {
  const response = await apiRequest("GET", `/files/client/${id}`, tableRequest);
  return response.data;
};

export const getFileByAgentId = async (
  id: string,
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetFiles> => {
  const response = await apiRequest("GET", `/files/agent/${id}`, tableRequest);
  return response.data;
};

export const createFile = async (data: IFileRequest) => {
  const user = getUser();
  const payload = {
    userId: user.id,
    agentId: data.agentId,
    clientId: data.clientId,
    purchaserId: data.purchaserId,
    vendorId: data.vendorId,
    branchId: data.branchId,
    clientCategory: data.clientCategory,
    refNumber: data.refNumber,
    clientType: data.clientType,
    fileType: data.fileType,
    billType: data.billType,
    propertyAddress: data.propertyAddress,
    spaPrice: data.spaPrice,
    fileRemark: data.fileRemark,
    picName: data.picName,
    completionDate: data.completionDate,
  };
  const response = await apiRequest<IFileRequest>("POST", "/files", payload);
  return response.data;
};

export const getFileById = async (id: string | undefined) => {
  const response = await apiRequest<IApiResponseGetFiles>(
    "GET",
    `/files/${id}`
  );
  return response.data;
};

export const editFile = async (id: string, data: IFileRequest) => {
  const payload = {
    ...(data.agentId && { agentId: data.agentId }),
    ...(data.clientId && { clientId: data.clientId }),
    ...(data.purchaserId && { purchaserId: data.purchaserId }),
    ...(data.vendorId && { vendorId: data.vendorId }),
    ...(data.branchId && { branchId: data.branchId }),
    clientCategory: data.clientCategory,
    refNumber: data.refNumber,
    clientType: data.clientType,
    fileType: data.fileType,
    billType: data.billType,
    propertyAddress: data.propertyAddress,
    spaPrice: data.spaPrice,
    fileRemark: data.fileRemark,
    picName: data.picName,
    completionDate: data.completionDate,
  };

  const response = await apiRequest<IFileRequest>(
    "PATCH",
    `/files/${id}`,
    payload
  );
  return response.data;
};

export const updateFileStatus = async (
  id: string,
  data: IFileStatusRequest,
  file?: File
) => {
  // Create FormData if file is provided
  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("status", data.status);
    formData.append("statusRemark", data.statusRemark || "");
    if (data.dateExecuted) {
      formData.append("dateExecuted", data.dateExecuted.toISOString());
    }

    const response = await apiRequest(
      "PATCH",
      `/files/${id}/status`,
      formData,
      true
    );
    return response.data;
  } else {
    // Use JSON payload if no file
    const payload = {
      status: data.status,
      statusRemark: data.statusRemark,
      dateExecuted: data.dateExecuted,
    };
    const response = await apiRequest("PATCH", `/files/${id}/status`, payload);
    return response.data;
  }
};

export const updateFileProfit = async (
  id: string,
  data: IFileProfitRequest
) => {
  const payload = {
    fileRevenue: data.fileRevenue,
    disbursement: data.disbursement,
    agentCommission: data.agentCommission,
    downpayment: data.downpayment,
  };
  const response = await apiRequest("PATCH", `/files/${id}/profit`, payload);
  return response.data;
};

export const downloadFileFromS3 = async (
  fileId: string,
  relativePath: string
) => {
  try {
    const blob = await downloadFile(
      `/files/download/${fileId}?filePath=${encodeURIComponent(relativePath)}`
    );
    const fileName = relativePath.split("/").pop() || "downloaded-file";
    openOrDownloadBlob(blob, fileName);
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

export const archiveFile = async (id: string) => {
  const response = await apiRequest("PATCH", `/files/${id}/archive`);
  return response.data;
};
